import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { canEditContent } from "../utils/permissions";
import { useLang } from "../contexts/LanguageContext";
import {
  VideoCamera, SpinnerGap, CheckCircle, Warning, Clock,
  SpeakerHigh, MagnifyingGlass, X, CaretDown, CaretUp, Eye,
  Waveform, ArrowRight, DownloadSimple, Play, PencilSimple, Check, ArrowsClockwise,
  UploadSimple, Trash, FileText,
} from "@phosphor-icons/react";
import ImportKnowledgeModal from "../components/ImportKnowledgeModal";
import DeleteVideoCascadeModal from "../components/DeleteVideoCascadeModal";
// Vincent XLS iter103 (items 1, 2, 18) — Modal KB partagé pour la validation
// cross-vidéo des scripts et procédures.
import { KnowledgeDetailModal } from "../components/knowledge/KnowledgeDetailModal";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";

import { API_BASE as API } from "../lib/apiBase";

/** Pass-through: segments are ALREADY regrouped by the backend (regroup_whisper_segments
 * with BLOCK_TARGET_SECONDS=30 / BLOCK_HARD_CAP=90). Previously this file did a 120s
 * client-side regroup which DIFFERED from VideoDetailPage → Vincent saw 2 different
 * transcripts for the same video. We now present exactly what the backend returns.
 * (Vincent bug 2026-05-02: "transcriptions différentes liste vs détail".)
 */
function groupSegments(segments) {
  if (!segments?.length) return [];
  return segments.map((seg) => {
    let text = (seg.text || "").trim();
    if (text && !/[.!?;:]$/.test(text)) text += ".";
    if (text) text = text.charAt(0).toUpperCase() + text.slice(1);
    return {
      speaker: seg.speaker || "?",
      start: seg.start || 0,
      end: seg.end || seg.start || 0,
      texts: [seg.text || ""],
      segIds: [seg.id],
      firstSegId: seg.id,
      text,
    };
  });
}

function formatTime(seconds) {
  if (!seconds && seconds !== 0) return "--:--";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const SPEAKER_COLORS = ["#002FA7", "#7c3aed", "#059669", "#dc2626", "#d97706", "#0891b2"];

export default function DataValidationPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  // Vincent #382 (Eric 2026-06-29 SPEC STRICK) — Catégories FR de l'Explorateur,
  // utilisées pour afficher la catégorie au lieu du `training_theme` codé en dur.
  const [categoriesById, setCategoriesById] = useState({});
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [transcriptData, setTranscriptData] = useState({});
  const [loadingTranscript, setLoadingTranscript] = useState(null);
  const [search, setSearch] = useState("");
  const [editingSpeaker, setEditingSpeaker] = useState(null); // {videoId, segIds, currentSpeaker}
  const [speakerOptions, setSpeakerOptions] = useState([]);
  const [savingSpeaker, setSavingSpeaker] = useState(false);
  // "Lancer toutes les étapes manquantes" SSE runner state — keyed by video.id
  // Vincent 2026-05-02 enhancement: 1-click chaining of url validation +
  // Q&R + scripts on a per-video basis with live progress.
  const [runner, setRunner] = useState(null); // {videoId, plannedStages, currentStage, message, results, done, error}
  // Vincent #138-140 (Eric 2026-05-16) — Modal d'import de connaissances
  const [importOpen, setImportOpen] = useState(false);

  // Eric 2026-06-25 (iter117 B2) — migré vers canEditContent. La logique
  // précédente (org_role === "admin" || is_founder) excluait à tort
  // concepteur/ceo/president/vp ; canEditContent unifie sur le whitelist
  // canonique aligné backend require_editor.
  const isAdmin = canEditContent(user);

  // Vincent #275/#291 (2026-06-11) — Modale avec liste à cocher des éléments à
  // conserver (remplace l'ancien enchaînement window.confirm/window.prompt).
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Vincent #304 (2026-06-12) — Onglet Vidéos / Textes.
  const [activeKind, setActiveKind] = useState("video"); // "video" | "text" | "scripts" | "procedures"
  // Vincent XLS iter103 (items 1, 2) — Onglets Scripts + Procédures cross-vidéo
  // pour la Validation des données. On charge les KU du type sélectionné en
  // priorisant les brouillons (status=pending_review). Cliquer une ligne ouvre
  // le KnowledgeDetailModal (même UI que la KB → Vincent #18).
  const [kuList, setKuList] = useState([]);
  const [kuModal, setKuModal] = useState(null); // KU sélectionnée pour le modal

  // Eric (2026-06-21) — Fonction nommée pour pouvoir recharger la liste après
  // unstick-all / batch generation (corrige le bug `loadVideos is not defined`).
  const loadVideos = async () => {
    try {
      const { data } = await axios.get(`${API}/api/videos?limit=200&kind=${activeKind}`, { withCredentials: true });
      setVideos(data?.items || []);
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => {
    setLoading(true);
    // Vincent #382 — charge les catégories FR de l'Explorateur (1 fois, indep
    // du kind affiché). Utilisé pour afficher le nom de catégorie au lieu de
    // `training_theme` ("sales", "general"…).
    axios.get(`${API}/api/content-categories`, { withCredentials: true })
      .then((r) => {
        const cats = Array.isArray(r.data) ? r.data : (r.data?.categories || []);
        const m = {};
        for (const c of cats) m[c.id] = c.name;
        setCategoriesById(m);
      })
      .catch(() => {});
    if (activeKind === "scripts" || activeKind === "procedures") {
      const kuType = activeKind === "scripts" ? "script" : "procedure";
      axios.get(`${API}/api/knowledge?type=${kuType}`, { withCredentials: true })
        .then(r => {
          const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
          // Tri : brouillons (pending_review) en premier, puis par date desc
          items.sort((a, b) => {
            const aP = a.status === "pending_review" ? 0 : 1;
            const bP = b.status === "pending_review" ? 0 : 1;
            if (aP !== bP) return aP - bP;
            return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
          });
          setKuList(items);
        })
        .catch(() => setKuList([]))
        .finally(() => setLoading(false));
      return;
    }
    axios.get(`${API}/api/videos?limit=200&kind=${activeKind}`, { withCredentials: true })
      .then(r => setVideos(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    // Load known speakers for editing
    axios.get(`${API}/api/speakers`, { withCredentials: true })
      .then(r => setSpeakerOptions(r.data || []))
      .catch(() => {});
  }, [activeKind]);

  const loadTranscript = async (videoId) => {
    if (transcriptData[videoId]) {
      setExpandedVideo(expandedVideo === videoId ? null : videoId);
      return;
    }
    setLoadingTranscript(videoId);
    setExpandedVideo(videoId);
    try {
      const { data } = await axios.get(`${API}/api/videos/${videoId}`, { withCredentials: true });
      setTranscriptData(prev => ({ ...prev, [videoId]: data }));
    } catch (e) { console.error(e); }
    setLoadingTranscript(null);
  };

  const reassignSpeaker = async (videoId, segIds, newSpeaker) => {
    setSavingSpeaker(true);
    try {
      await axios.post(`${API}/api/videos/${videoId}/reassign-segments`, {
        segment_ids: segIds,
        new_speaker: newSpeaker,
      }, { withCredentials: true });
      // Reload transcript
      const { data } = await axios.get(`${API}/api/videos/${videoId}`, { withCredentials: true });
      setTranscriptData(prev => ({ ...prev, [videoId]: data }));
      setEditingSpeaker(null);
    } catch (e) { console.error(e); }
    setSavingSpeaker(false);
  };

  // Vincent #93 (2026-05-12) — Tri alpha + chrono des vidéos listées dans
  // Validation des données. Persisté dans localStorage par org pour que le tri
  // reste actif entre sessions.
  // Vincent #298 (2026-06-12) — Tri A→Z par défaut (aligné sur Explorateur),
  // et menu déroulant unique au lieu des 4 boutons.
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem("knoria_dv_sort") || "name_asc"; }
    catch { return "name_asc"; }
  });
  const changeSort = (next) => {
    setSortBy(next);
    try { localStorage.setItem("knoria_dv_sort", next); } catch {}
  };

  const filtered = videos
    .filter(v => !search || v.title?.toLowerCase().includes(search.toLowerCase()) || v.training_theme?.toLowerCase().includes(search.toLowerCase()))
    .slice()
    .sort((a, b) => {
      const ta = (a.title || "").toLowerCase();
      const tb = (b.title || "").toLowerCase();
      const da = a.created_at || "";
      const db = b.created_at || "";
      switch (sortBy) {
        case "name_asc": return ta.localeCompare(tb, "fr", { sensitivity: "base" });
        case "name_desc": return tb.localeCompare(ta, "fr", { sensitivity: "base" });
        case "date_asc": return da.localeCompare(db);
        case "date_desc":
        default: return db.localeCompare(da);
      }
    });

  /** Start the SSE chain that runs every missing pipeline stage on a video.
   * Bridges the backend's `data: {json}\n\n` frames into local state. */
  const runAllStages = async (video) => {
    if (runner && runner.videoId && !runner.done) return; // already running
    const ok = await confirmDialog(lang === "fr"
      ? `Lancer toutes les étapes manquantes pour "${video.title}" ? Le pipeline IA peut prendre 1-3 minutes.`
      : `Run all missing stages for "${video.title}"? AI pipeline may take 1-3 minutes.`);
    if (!ok) return;

    setRunner({ videoId: video.id, plannedStages: [], currentStage: null, message: "Démarrage…", results: {}, done: false, error: null });

    try {
      // Vincent P1.10 (Eric 2026-07-31 SPEC STRICK PROD) — SSE via fetch avec
      // credentials: "include" → cookie httpOnly envoyé automatiquement.
      const resp = await fetch(`${API}/api/videos/${video.id}/run-all-stages-stream`, {
        method: "GET",
        credentials: "include",
      });
      if (!resp.ok || !resp.body) {
        const t = await resp.text().catch(() => "");
        throw new Error(t || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep;
        while ((sep = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const lines = frame.split("\n").filter((l) => l.startsWith("data:"));
          if (!lines.length) continue;
          const payload = lines.map((l) => l.slice(5).trim()).join("\n");
          let evt;
          try { evt = JSON.parse(payload); } catch { continue; }
          setRunner((prev) => {
            if (!prev) return prev;
            switch (evt.event) {
              case "start":
                return { ...prev, plannedStages: evt.planned_stages || [], message: (evt.planned_stages || []).length === 0 ? (lang === "fr" ? "Toutes les étapes sont déjà complétées" : "All stages already done") : (lang === "fr" ? `${evt.total} étape(s) planifiée(s)` : `${evt.total} stage(s) planned`) };
              case "stage_start":
                return { ...prev, currentStage: evt.stage, message: (lang === "fr" ? `Étape ${evt.index}/${evt.total} : ${evt.stage}` : `Stage ${evt.index}/${evt.total}: ${evt.stage}`) };
              case "stage_progress":
                return { ...prev, message: evt.message };
              case "stage_done":
                return { ...prev, results: { ...prev.results, [evt.stage]: evt.result } };
              case "stage_skipped":
                return { ...prev, results: { ...prev.results, [evt.stage]: { skipped: evt.reason } } };
              case "complete":
                return { ...prev, done: true, message: lang === "fr" ? "Terminé" : "Done" };
              case "error":
                return { ...prev, error: evt.message, message: `❌ ${evt.message}` };
              default:
                return prev;
            }
          });
        }
      }
      // Refresh the videos list to update stage badges
      const { data } = await axios.get(`${API}/api/videos?limit=200`, { withCredentials: true });
      setVideos(data?.items || []);
    } catch (e) {
      setRunner((prev) => prev ? { ...prev, error: e.message, done: true } : prev);
    }
    // Auto-clear after 6s if no error so user has time to read the result
    setTimeout(() => {
      setRunner((prev) => (prev && prev.done && !prev.error) ? null : prev);
    }, 6000);
  };

  const exportTranscript = (video, groups) => {
    const title = video.title || "transcription";
    const date = new Date(video.created_at).toLocaleDateString("fr-CA");
    const speakers = [...new Set(groups.map(g => g.speaker))];
    let content = `KNORIA — Transcription complète\n`;
    content += `${"=".repeat(60)}\n`;
    content += `Video : ${title}\n`;
    content += `Date : ${date}\n`;
    content += `Catégorie : ${video.training_theme || "general"}\n`;
    content += `Locuteurs : ${speakers.join(", ")}\n`;
    content += `${"=".repeat(60)}\n\n`;
    let currentSpeaker = "";
    for (const g of groups) {
      if (g.speaker !== currentSpeaker) {
        content += `\n[${g.speaker}]\n`;
        currentSpeaker = g.speaker;
      }
      content += `${formatTime(g.start)}  ${g.text}\n`;
    }
    content += `\n${"=".repeat(60)}\n`;
    content += `Généré par KNORIA — Knowledge Empowered\n`;
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `KNORIA_Transcription_${title.replace(/[^a-zA-Z0-9-_ ]/g, "").trim()}_${date}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusIcon = (status) => {
    if (status === "complete") return <CheckCircle size={16} className="text-teal-500" weight="fill" />;
    if (status === "failed") return <Warning size={16} className="text-red-500" />;
    return <Clock size={16} className="text-amber-500" />;
  };

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={32} className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="data-validation-page">
      {/* Header — Vincent #133 (Eric 2026-05-16) — Harmonisé avec File de
          validation rapide : icon w-12 h-12 rounded-2xl + couleur KNORIA + font Outfit. */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#001a5e] flex items-center justify-center">
          <Eye size={22} weight="duotone" className="text-white" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-xl font-bold font-['Outfit']" data-testid="validation-title">
            {lang === "fr" ? "Validation des données" : "Data Validation"}
          </h1>
          <p className="text-xs text-muted-foreground">
            {lang === "fr"
              ? "Vérifiez les textes, vidéos, transcriptions, etc. avant exploitation"
              : "Verify texts, videos, transcriptions, etc. before use"}
          </p>
        </div>
        {/* Vincent #138-140 (Eric 2026-05-16) — Import depuis fichier */}
        {isAdmin && (
          <button
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-[#002FA7] text-white hover:bg-[#002280] transition-colors shrink-0"
            data-testid="open-import-modal"
            title={lang === "fr"
              ? "Importer Q&R, procédures, scripts, objections ou concepts depuis un fichier (CSV/JSON/MD/.docx/.xlsx)"
              : "Import knowledge from a file (CSV/JSON/MD/.docx/.xlsx)"}
          >
            <UploadSimple size={14} weight="bold" />
            {lang === "fr" ? "Importer depuis fichier" : "Import from file"}
          </button>
        )}
      </div>

      <ImportKnowledgeModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { /* could trigger refresh, but the list above is videos */ }}
      />

      {/* Vincent #275/#291 — Modale d'effacement avec liste à cocher de conservation */}
      {deleteTarget && (
        <DeleteVideoCascadeModal
          video={deleteTarget}
          lang={lang}
          onClose={() => setDeleteTarget(null)}
          onDeleted={(vid) => setVideos((prev) => prev.filter((x) => x.id !== vid))}
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-2xl font-bold font-['Outfit']">{videos.length}</p>
          <p className="text-xs text-muted-foreground">{
            // Vincent #340/#341 (Eric 2026-06-18 SPEC STRICK ULTRA) — label dynamique
            lang === "fr"
              ? (activeKind === "text" ? "Fichiers (total)" : "Vidéos (total)")
              : (activeKind === "text" ? "Files (total)" : "Videos (total)")
          }</p>
        </div>
        {activeKind !== "text" && (
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-2xl font-bold font-['Outfit'] text-teal-500">{videos.filter(v => v.pipeline_status === "complete").length}</p>
          <p className="text-xs text-muted-foreground">{lang === "fr" ? "Transcrites" : "Transcribed"}</p>
        </div>
        )}
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-2xl font-bold font-['Outfit'] text-amber-500">{videos.filter(v => v.pipeline_status === "processing" || v.pipeline_status === "pending").length}</p>
          <p className="text-xs text-muted-foreground">{lang === "fr" ? "En cours" : "Processing"}</p>
          {isAdmin && videos.filter(v => v.pipeline_status === "processing" || v.pipeline_status === "pending").length > 0 && (
            <button
              onClick={async () => {
                const stuck = videos.filter(v => v.pipeline_status === "processing" || v.pipeline_status === "pending");
                const ok = await confirmDialog(lang === "fr"
                  ? `Relancer ${stuck.length} vidéo(s) bloquée(s) en traitement ?\n\nCela remettra dans la file toutes les vidéos en "processing" ou "pending" (cause fréquente : un Deploy a tué leur job en cours).`
                  : `Unstick ${stuck.length} processing video(s)? Re-queues all stuck videos.`);
                if (!ok) return;
                try {
                  const { data } = await axios.post(`${API}/api/videos/unstick-all`, {}, { withCredentials: true });
                  toast.success(lang === "fr" ? `${data.requeued} vidéo(s) remise(s) en file` : `${data.requeued} requeued`);
                  setTimeout(() => loadVideos(), 1500);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Échec");
                }
              }}
              className="mt-1.5 text-[10px] px-2 py-0.5 rounded bg-amber-500 text-white font-semibold hover:bg-amber-600 inline-flex items-center gap-1"
              data-testid="unstick-all-btn"
              title={lang === "fr" ? "Relancer toutes les vidéos bloquées" : "Unstick all stuck videos"}
            >
              <ArrowsClockwise size={10} weight="bold" />
              {lang === "fr" ? "Tout relancer" : "Unstick all"}
            </button>
          )}
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-2xl font-bold font-['Outfit'] text-red-500">{videos.filter(v => v.pipeline_status === "failed").length}</p>
          <p className="text-xs text-muted-foreground">{lang === "fr" ? "Échouées" : "Failed"}</p>
        </div>
      </div>

      {/* Banner: visible explainer if there are stuck videos (Vincent 2026-05-02) */}
      {isAdmin && videos.filter(v => v.pipeline_status === "processing" || v.pipeline_status === "pending").length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-xs" data-testid="stuck-videos-banner">
          <div className="flex items-start gap-2">
            <Warning size={16} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900">
                {lang === "fr"
                  ? "Vidéos bloquées en traitement"
                  : "Videos stuck in processing"}
              </p>
              <p className="text-amber-800 mt-0.5 leading-relaxed">
                {lang === "fr"
                  ? "Cela arrive quand un Deploy intervient pendant le pipeline (Whisper/AssemblyAI/fusion) — le job est tué mais le statut reste « en cours ». Cliquez « Tout relancer » dans la stat en haut, ou « Relancer » sur chaque vidéo individuellement."
                  : "This happens when a Deploy kills in-flight pipeline jobs. Click 'Unstick all' above or 'Retry' per video."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Vincent #304 (2026-06-12) — Onglets Vidéos / Textes en tête de page. */}
      <div className="flex border-b border-border" data-testid="validation-kind-tabs">
        <button
          onClick={() => setActiveKind("video")}
          className={`relative px-4 py-2 text-sm font-semibold transition-colors ${
            activeKind === "video" ? "text-[#002FA7]" : "text-zinc-500 hover:text-zinc-800"
          }`}
          data-testid="validation-tab-videos"
        >
          {lang === "fr" ? "Vidéos" : "Videos"}
          {activeKind === "video" && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#002FA7] rounded-full" />}
        </button>
        <button
          onClick={() => setActiveKind("text")}
          className={`relative px-4 py-2 text-sm font-semibold transition-colors ${
            activeKind === "text" ? "text-[#002FA7]" : "text-zinc-500 hover:text-zinc-800"
          }`}
          data-testid="validation-tab-texts"
        >
          {lang === "fr" ? "Textes" : "Texts"}
          {activeKind === "text" && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#002FA7] rounded-full" />}
        </button>
        {/* Vincent #339 (Eric 2026-06-18 SPEC STRICK ULTRA) — Onglets "Scripts" et
            "Procédures" RETIRÉS de la Validation des données (déjà affichés dans
            la Base de connaissances). */}
      </div>

      {/* Search + Sort (Vincent #93 — tri alpha/chrono) */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[280px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={lang === "fr"
              ? (activeKind === "text" ? "Rechercher un fichier..." : "Rechercher une vidéo...")
              : (activeKind === "text" ? "Search a file..." : "Search a video...")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full h-10 pl-10 pr-10 text-sm bg-card border border-border rounded-xl focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="validation-search"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1" data-testid="validation-sort-menu">
          <span className="text-[11px] text-muted-foreground mr-1">{lang === "fr" ? "Trier :" : "Sort:"}</span>
          <select
            value={sortBy}
            onChange={(e) => changeSort(e.target.value)}
            className="px-3 py-2 bg-card border border-border rounded-lg text-sm"
            data-testid="validation-sort-select"
            title={lang === "fr" ? "Trier les vidéos" : "Sort videos"}
          >
            <option value="name_asc">{lang === "fr" ? "Nom A → Z" : "Name A → Z"}</option>
            <option value="name_desc">{lang === "fr" ? "Nom Z → A" : "Name Z → A"}</option>
            <option value="date_desc">{lang === "fr" ? "Date ↓ (récent → ancien)" : "Date ↓ (recent → old)"}</option>
            <option value="date_asc">{lang === "fr" ? "Date ↑ (ancien → récent)" : "Date ↑ (old → recent)"}</option>
          </select>
        </div>
      </div>

      {/* Video List */}
      {(activeKind === "video" || activeKind === "text") && (
      <div className="space-y-2" data-testid="video-list">
        {filtered.map(video => {
          const isExpanded = expandedVideo === video.id;
          const td = transcriptData[video.id];
          const segments = td?.segments || [];
          const transcript = td?.transcript;
          const groups = groupSegments(segments);
          const uniqueSpeakers = [...new Set(segments.map(s => s.speaker).filter(Boolean))];

          return (
            <div key={video.id} className="bg-card border border-border rounded-xl overflow-hidden" data-testid={`video-row-${video.id}`}>
              {/* Video header — Vincent (Eric 2026-06-21) : ancien <button>
                  englobant remplacé par <div role='button'> pour éviter le
                  warning d'hydration React (le bouton "Supprimer" était nesté
                  dans le button du toggle). */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => loadTranscript(video.id)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); loadTranscript(video.id); } }}
                className="w-full flex items-center gap-3 p-4 hover:bg-secondary/50 transition-colors text-left cursor-pointer"
                data-testid={`video-toggle-${video.id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                  {/* Vincent #340 (Eric 2026-06-18 SPEC STRICK ULTRA) — Icône doc pour fichiers texte */}
                  {activeKind === "text" ? (
                    <FileText size={20} className="text-muted-foreground" />
                  ) : (
                    <VideoCamera size={20} className="text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {statusIcon(video.pipeline_status)}
                    <h3 className="font-semibold text-sm truncate">{video.title}</h3>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5">
                    <span className="capitalize" data-testid={`video-category-${video.id}`}>
                      {/* Vincent #382 (Eric 2026-06-29 SPEC STRICK) — Catégorie FR
                          de l'Explorateur (si renseignée), sinon repli sur le
                          legacy `training_theme` ("general", "sales"…). */}
                      {categoriesById[video.category_id] || video.training_theme || "general"}
                    </span>
                    <span>{new Date(video.created_at).toLocaleDateString("fr-CA")}</span>
                    <span>{video.file_size ? `${(video.file_size / (1024 * 1024)).toFixed(1)} MB` : "—"}</span>
                  </div>
                  {/* Pipeline-stage badges.
                      Vincent #340/#341 (Eric 2026-06-18 SPEC STRICK ULTRA) :
                       - Textes : on retire les étapes vidéo (transcription/diarisation/fusion/corrections)
                       - Vidéos : on ajoute "concepts" + "objections" (déjà calculés côté serveur). */}
                  {video.stages_completed && (
                    <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`stages-badges-${video.id}`}>
                      {(activeKind === "text"
                        ? [
                            { key: "qa",             label: "Q&R" },
                            { key: "scripts",        label: "scripts" },
                            { key: "procedures",     label: lang === "fr" ? "procédures" : "procedures" },
                            { key: "concepts",       label: lang === "fr" ? "concepts" : "concepts" },
                            { key: "objections",     label: lang === "fr" ? "objections" : "objections" },
                          ]
                        : [
                            { key: "transcription",  label: lang === "fr" ? "transcription" : "transcription" },
                            { key: "diarisation",    label: lang === "fr" ? "diarisation" : "diarization" },
                            { key: "fusion",         label: lang === "fr" ? "fusion" : "fusion" },
                            { key: "correction_fr",  label: lang === "fr" ? "correction FR" : "FR fix" },
                            { key: "correction_url", label: lang === "fr" ? "correction URL" : "URL fix" },
                            { key: "qa",             label: "Q&R" },
                            { key: "scripts",        label: "scripts" },
                            { key: "procedures",     label: lang === "fr" ? "procédures" : "procedures" },
                            { key: "concepts",       label: lang === "fr" ? "concepts" : "concepts" },
                            { key: "objections",     label: lang === "fr" ? "objections" : "objections" },
                          ]
                      ).map(({ key, label }) => {
                        const done = video.stages_completed[key];
                        return (
                          <span
                            key={key}
                            className={`px-1.5 py-0.5 rounded text-[9px] font-semibold border ${
                              done
                                ? "bg-teal-50 border-teal-300 text-teal-700"
                                : "bg-zinc-50 border-zinc-200 text-zinc-400"
                            }`}
                            title={done ? (lang === "fr" ? `${label} : terminé` : `${label}: done`) : (lang === "fr" ? `${label} : à faire` : `${label}: pending`)}
                          >
                            {done ? "✓" : "○"} {label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {/* Retry pipeline button (admin only). Shown when the
                      pipeline is stuck (failed) OR has been processing for
                      a long time without completing. Vincent 2026-05-02:
                      "il faut une fonction pour relancer le processus quand
                      il est bloqué dans les étapes du pipeline". */}
                  {isAdmin && (video.pipeline_status === "failed" || video.pipeline_status === "processing") && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        const ok = await confirmDialog(lang === "fr"
                          ? `Relancer le traitement de "${video.title}" ? La pipeline (transcription, diarisation, fusion) va recommencer depuis le début.`
                          : `Retry processing for "${video.title}"? Pipeline will restart from scratch.`);
                        if (!ok) return;
                        try {
                          await axios.post(`${API}/api/videos/${video.id}/reprocess`, {}, { withCredentials: true });
                          toast.success(lang === "fr" ? "Pipeline relancée — peut prendre quelques minutes" : "Pipeline restarted — may take a few minutes");
                          // Refresh the list after 2s so the user sees the new status
                          setTimeout(() => loadVideos(), 2000);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || (lang === "fr" ? "Échec de la relance" : "Retry failed"));
                        }
                      }}
                      className="text-[10px] px-2 py-1 rounded-full bg-amber-600 text-white font-semibold hover:bg-amber-700 inline-flex items-center gap-1"
                      data-testid={`retry-pipeline-btn-${video.id}`}
                      title={lang === "fr" ? "Relancer la pipeline (transcription, diarisation, fusion)" : "Retry pipeline (transcription, diarization, fusion)"}
                    >
                      <ArrowsClockwise size={11} weight="bold" />
                      {lang === "fr" ? "Relancer" : "Retry"}
                    </button>
                  )}
                  {/* Run-all-missing-stages button (admin only). Only shown
                      when the video pipeline is complete AND at least one
                      auto-runnable stage is missing. */}
                  {isAdmin && video.pipeline_status === "complete" && video.stages_completed && (
                    !video.stages_completed.qa || !video.stages_completed.scripts || !video.stages_completed.correction_url
                  ) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); runAllStages(video); }}
                      disabled={runner && runner.videoId === video.id && !runner.done}
                      className="text-[10px] px-2 py-1 rounded-full bg-[#002FA7] text-white font-semibold hover:bg-[#001F7A] disabled:opacity-50 disabled:cursor-wait inline-flex items-center gap-1"
                      data-testid={`run-all-stages-btn-${video.id}`}
                      title={lang === "fr" ? "Lancer URL + Q&R + Scripts (1 clic)" : "Run URL + Q&A + Scripts (1 click)"}
                    >
                      {runner && runner.videoId === video.id && !runner.done
                        ? <SpinnerGap size={11} className="animate-spin" />
                        : <Play size={11} weight="fill" />}
                      {lang === "fr" ? "Lancer manquant" : "Run missing"}
                    </button>
                  )}
                  <span
                    onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/videos/${video.id}`); }}
                    className="text-xs text-primary hover:underline flex items-center gap-1 cursor-pointer"
                    data-testid={`video-link-${video.id}`}
                  >
                    {lang === "fr" ? "Ouvrir" : "Open"} <ArrowRight size={10} />
                  </span>
                  {/* Vincent #258 (Eric 2026-02-06) — Bouton suppression vidéo + cascade
                      (Q&R, scripts, procédures, concepts). Double confirmation. */}
                  {isAdmin && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(video); }}
                      className="text-red-600 hover:bg-red-50 rounded p-1 transition-colors"
                      data-testid={`btn-delete-video-${video.id}`}
                      title={lang === "fr" ? "Supprimer la vidéo et toutes ses connaissances" : "Delete video and all knowledge"}
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                  {isExpanded ? <CaretUp size={16} className="text-muted-foreground" /> : <CaretDown size={16} className="text-muted-foreground" />}
                </div>
              </div>

              {/* Live progress panel for the running chain (this video only) */}
              {runner && runner.videoId === video.id && (
                <div
                  className={`mx-4 mt-2 mb-2 px-3 py-2 rounded border text-[11px] ${
                    runner.error ? "bg-red-50 border-red-300 text-red-900"
                    : runner.done ? "bg-teal-50 border-teal-300 text-teal-900"
                    : "bg-blue-50 border-blue-300 text-blue-900"
                  }`}
                  data-testid={`run-all-progress-${video.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {!runner.done && !runner.error && <SpinnerGap size={13} className="animate-spin shrink-0" />}
                      <span className="truncate font-medium">{runner.message}</span>
                    </div>
                    {(runner.done || runner.error) && (
                      <button onClick={() => setRunner(null)} className="text-[10px] opacity-70 hover:opacity-100 shrink-0">✕</button>
                    )}
                  </div>
                  {runner.plannedStages?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {runner.plannedStages.map((s) => {
                        const isDone = !!runner.results?.[s];
                        const isCurrent = runner.currentStage === s && !isDone && !runner.done;
                        return (
                          <span
                            key={s}
                            className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${
                              isDone ? "bg-teal-200 text-teal-900"
                              : isCurrent ? "bg-blue-200 text-blue-900 animate-pulse"
                              : "bg-zinc-200 text-zinc-600"
                            }`}
                          >
                            {isDone ? "✓ " : isCurrent ? "▸ " : "○ "}{s}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Expanded transcript */}
              {isExpanded && (
                <div className="border-t border-border px-4 pb-4" data-testid={`video-detail-${video.id}`}>
                  {loadingTranscript === video.id ? (
                    <div className="flex items-center gap-2 py-6 justify-center">
                      <SpinnerGap size={18} className="animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">{lang === "fr" ? "Chargement..." : "Loading..."}</span>
                    </div>
                  ) : !transcript ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      {lang === "fr" ? "Aucune transcription disponible" : "No transcript available"}
                    </p>
                  ) : (
                    <div className="pt-3 space-y-3">
                      {/* Speaker summary + Export */}
                      <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-xs font-semibold text-muted-foreground">
                          {lang === "fr" ? "Locuteurs:" : "Speakers:"}
                        </span>
                        {uniqueSpeakers.map((sp, idx) => (
                          <span key={sp} className="text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1" style={{ backgroundColor: SPEAKER_COLORS[idx % SPEAKER_COLORS.length] + "15", color: SPEAKER_COLORS[idx % SPEAKER_COLORS.length] }}>
                            <Waveform size={10} /> {sp}
                          </span>
                        ))}
                        <span className="text-[10px] text-muted-foreground ml-2">
                          {groups.length} {lang === "fr" ? "blocs" : "blocks"} | {segments.length} seg
                          {transcript.cross_validated && <span className="text-teal-500 ml-1">| {lang === "fr" ? "Valide croise" : "Cross-validated"}</span>}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); exportTranscript(video, groups); }}
                          className="ml-auto flex items-center gap-1 text-xs text-primary hover:underline px-2 py-1 border border-primary/20 rounded-lg hover:bg-primary/5"
                          data-testid={`export-transcript-${video.id}`}
                        >
                          <DownloadSimple size={12} />
                          {lang === "fr" ? "Exporter (.txt)" : "Export (.txt)"}
                        </button>
                      </div>

                      {/* Hint */}
                      <p className="text-[10px] text-muted-foreground italic">
                        {lang === "fr"
                          ? "Cliquez sur le timecode pour ouvrir la video. Cliquez sur le nom du locuteur pour le modifier."
                          : "Click timecode to open video. Click speaker name to edit."}
                      </p>

                      {/* Grouped transcript */}
                      <div className="max-h-[500px] overflow-y-auto border border-border rounded-lg divide-y divide-border" data-testid={`transcript-segments-${video.id}`}>
                        {groups.map((g, i) => {
                          const speakerIdx = uniqueSpeakers.indexOf(g.speaker);
                          const color = SPEAKER_COLORS[speakerIdx >= 0 ? speakerIdx % SPEAKER_COLORS.length : 5];
                          const isEditing = editingSpeaker?.videoId === video.id && editingSpeaker?.groupIdx === i;
                          const timeSeconds = Math.floor(g.start || 0);

                          return (
                            <div key={i} className="flex gap-2 sm:gap-3 px-2 sm:px-3 py-2.5 hover:bg-primary/5 transition-colors group" data-testid={`group-${i}`}>
                              {/* Timecode — clickable to navigate */}
                              <button
                                onClick={() => navigate(`/dashboard/videos/${video.id}?t=${timeSeconds}`)}
                                className="w-14 sm:w-16 shrink-0 text-[10px] text-primary tabular-nums pt-0.5 hover:font-bold flex items-center gap-0.5 text-left"
                                title={lang === "fr" ? `Lire la video a ${formatTime(g.start)}` : `Play video at ${formatTime(g.start)}`}
                                data-testid={`time-${i}`}
                              >
                                <Play size={8} className="opacity-0 group-hover:opacity-100 shrink-0" />
                                {formatTime(g.start)}
                              </button>

                              {/* Speaker name — clickable to edit */}
                              <div className="w-28 sm:w-36 shrink-0">
                                {isEditing ? (
                                  <div className="flex items-center gap-1">
                                    <select
                                      className="text-[11px] border border-primary rounded px-1 py-0.5 bg-white w-full"
                                      defaultValue={g.speaker}
                                      autoFocus
                                      onChange={(e) => {
                                        reassignSpeaker(video.id, g.segIds, e.target.value);
                                      }}
                                      onBlur={() => setEditingSpeaker(null)}
                                      data-testid={`speaker-edit-select-${i}`}
                                    >
                                      {uniqueSpeakers.map(sp => (
                                        <option key={sp} value={sp}>{sp}</option>
                                      ))}
                                      {speakerOptions.filter(sp => !uniqueSpeakers.includes(sp.name)).map(sp => (
                                        <option key={sp.id} value={sp.name}>{sp.name}</option>
                                      ))}
                                      <option value="Speaker 1">Speaker 1</option>
                                      <option value="Speaker 2">Speaker 2</option>
                                    </select>
                                    {savingSpeaker && <SpinnerGap size={10} className="animate-spin text-primary shrink-0" />}
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setEditingSpeaker({ videoId: video.id, groupIdx: i, segIds: g.segIds, currentSpeaker: g.speaker })}
                                    className="text-[11px] font-semibold truncate flex items-center gap-1 hover:underline text-left w-full"
                                    style={{ color }}
                                    title={lang === "fr" ? "Cliquer pour modifier le locuteur" : "Click to change speaker"}
                                    data-testid={`speaker-btn-${i}`}
                                  >
                                    <SpeakerHigh size={10} weight="fill" className="shrink-0" />
                                    <span className="truncate">{g.speaker}</span>
                                    <PencilSimple size={8} className="opacity-0 group-hover:opacity-100 shrink-0 ml-auto" />
                                  </button>
                                )}
                              </div>

                              {/* Text */}
                              <div className="flex-1 text-sm leading-relaxed min-w-0">
                                {g.text}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* Vincent XLS iter103 (items 1, 2) — Liste Scripts / Procédures cross-vidéo */}
      {(activeKind === "scripts" || activeKind === "procedures") && (
        <div className="space-y-2" data-testid={`ku-list-${activeKind}`}>
          {kuList
            .filter(ku => !search || (ku.title || "").toLowerCase().includes(search.toLowerCase()))
            .map(ku => {
              const isPending = ku.status === "pending_review";
              const isApproved = ku.status === "approved";
              const isRejected = ku.status === "rejected";
              return (
                <button
                  key={ku.id}
                  onClick={() => setKuModal(ku)}
                  className="w-full text-left bg-card border border-border rounded-xl px-4 py-3 hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-all"
                  data-testid={`ku-row-${ku.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-sm ${
                          isPending ? "bg-amber-100 text-amber-800 border border-amber-200"
                          : isApproved ? "bg-teal-100 text-teal-800 border border-teal-200"
                          : isRejected ? "bg-rose-100 text-rose-800 border border-rose-200"
                          : "bg-zinc-100 text-zinc-700 border border-zinc-200"
                        }`}>
                          {isPending ? (lang === "fr" ? "BROUILLON" : "DRAFT")
                            : isApproved ? (lang === "fr" ? "PUBLIÉ" : "PUBLISHED")
                            : isRejected ? (lang === "fr" ? "REFUSÉ" : "REJECTED")
                            : ku.status}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {activeKind === "scripts" ? (lang === "fr" ? "Script" : "Script") : (lang === "fr" ? "Procédure" : "Procedure")}
                        </span>
                        {ku.edited_post_approval && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded-sm bg-amber-100 text-amber-800 border border-amber-200">édité</span>
                        )}
                      </div>
                      <h4 className="text-sm font-semibold text-foreground line-clamp-2">{ku.title || (lang === "fr" ? "(sans titre)" : "(untitled)")}</h4>
                      <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">
                        {ku.content?.summary || ku.content?.question || ku.content?.script_title || ""}
                      </p>
                    </div>
                    <ArrowRight size={16} className="text-muted-foreground flex-shrink-0 mt-1" />
                  </div>
                </button>
              );
            })}
          {kuList.length === 0 && (
            <div className="text-center py-12 text-muted-foreground text-sm">
              {lang === "fr"
                ? (activeKind === "scripts" ? "Aucun script à valider" : "Aucune procédure à valider")
                : (activeKind === "scripts" ? "No script to validate" : "No procedure to validate")}
            </div>
          )}
        </div>
      )}

      {/* Modal KB (Vincent #18) — édition d'un script/procédure avec la même UI que dans la Base de connaissances */}
      {kuModal && (
        <KnowledgeDetailModal
          unit={kuModal}
          videoTitle=""
          formatTimestamp={(s) => formatTime(s || 0)}
          onClose={() => setKuModal(null)}
          isAdmin={isAdmin}
          onUpdated={() => {
            // Reload the list
            const kuType = activeKind === "scripts" ? "script" : "procedure";
            axios.get(`${API}/api/knowledge?type=${kuType}`, { withCredentials: true })
              .then(r => {
                const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
                items.sort((a, b) => {
                  const aP = a.status === "pending_review" ? 0 : 1;
                  const bP = b.status === "pending_review" ? 0 : 1;
                  if (aP !== bP) return aP - bP;
                  return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
                });
                setKuList(items);
              });
          }}
        />
      )}

      {(activeKind === "video" || activeKind === "text") && filtered.length === 0 && (
        <div className="text-center py-12 text-muted-foreground text-sm">
          {lang === "fr" ? "Aucune vidéo trouvée" : "No videos found"}
        </div>
      )}
    </div>
  );
}
